import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Radio, message } from 'antd';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import './index.scss';

// 临时限速类型枚举
enum TempSpeedLimitType {
  LIGHT_RAIN = 'LIGHT_RAIN', // 小雨
  MODERATE_RAIN = 'MODERATE_RAIN', // 中雨
  HEAVY_RAIN_OR_SNOW = 'HEAVY_RAIN_OR_SNOW', // 大雨/雪
  OTHER = 'OTHER', // 自定义
}

interface SpeedLimitInfo {
  xgflags: number | string | null; // 车端conf限速值
  isTempSpeedLimitEnabled: boolean; // 是否开启临时限速
  tempSpeedLimitType: TempSpeedLimitType | string | undefined; // 临时限速类型
  tempSpeedLimit: number | undefined; // 临时限速值
}

interface SpeedLimitModalProps {
  visible: boolean;
  vehicleName: string;
  handleCloseLimitSpeedModal: () => void;
}

const SpeedLimitModal: React.FC<SpeedLimitModalProps> = ({
  vehicleName,
  visible,
  handleCloseLimitSpeedModal,
}) => {
  const [form] = Form.useForm();
  const [speedLimitInfo, setSpeedLimitInfo] = useState<SpeedLimitInfo>({
    xgflags: null,
    isTempSpeedLimitEnabled: false,
    tempSpeedLimitType: undefined,
    tempSpeedLimit: undefined,
  });
  const [loading, setLoading] = useState(false);

  const tempSpeedLimitTypes = [
    { label: '10（大雨/雪）', value: TempSpeedLimitType.HEAVY_RAIN_OR_SNOW },
    { label: '15（中雨）', value: TempSpeedLimitType.MODERATE_RAIN },
    { label: '20（小雨）', value: TempSpeedLimitType.LIGHT_RAIN },
    { label: '自定义', value: TempSpeedLimitType.OTHER },
  ];

  const tempSpeedLimitValueMap = new Map([
    [TempSpeedLimitType.HEAVY_RAIN_OR_SNOW, 10],
    [TempSpeedLimitType.MODERATE_RAIN, 15],
    [TempSpeedLimitType.LIGHT_RAIN, 20],
  ]);

  // 获取车辆限速信息
  const fetchSpeedLimitInfo = async () => {
    if (!vehicleName) {
      message.error('无法获取车辆信息');
      return;
    }
    try {
      const res = await request({
        method: 'POST',
        path: '/monitor/web/basic/getTempSpeedLimitInfo',
        body: {
          vehicleName: vehicleName,
        },
      });

      if (res.code === HttpStatusCode.Success) {
        setSpeedLimitInfo(res.data);
        form.setFieldsValue(res.data);
      } else {
        message.error(res.message || '获取车辆限速信息失败');
      }
    } catch (error) {
      message.error('获取车辆限速信息失败');
    }
  };
  useEffect(() => {
    if (visible) {
      fetchSpeedLimitInfo();
    }
  }, [visible]);


  const handleTempSpeedLimitEnabledChange = (value: boolean) => {
    form.setFieldsValue({
      tempSpeedLimitType: undefined,
      tempSpeedLimit: undefined,
    });
    setSpeedLimitInfo({
      ...speedLimitInfo,
      isTempSpeedLimitEnabled: value,
      tempSpeedLimitType: undefined,
      tempSpeedLimit: undefined,
    });
  };
  const handleTempSpeedLimitTypeChange = (value: TempSpeedLimitType) => {
    const speedValue =
      value === TempSpeedLimitType.OTHER
        ? undefined
        : tempSpeedLimitValueMap.get(value);
    form.setFieldsValue({ tempSpeedLimit: speedValue });
    setSpeedLimitInfo((prev) => {
      if (!prev) return prev;
      const newSpeedLimitInfo = {
        ...prev,
        tempSpeedLimitType: value,
        tempSpeedLimit: speedValue,
      };
      return newSpeedLimitInfo;
    });
  };
  const onValuesChange = (changedValues: any) => {
    // 更新speedLimitInfo状态
    setSpeedLimitInfo((prev) => {
      if (!prev) return null;
      const newSpeedLimitInfo = {
        ...prev,
        ...changedValues,
      };
      return newSpeedLimitInfo;
    });

    if ('isTempSpeedLimitEnabled' in changedValues) {
      handleTempSpeedLimitEnabledChange(changedValues.isTempSpeedLimitEnabled);
    }
    if ('tempSpeedLimitType' in changedValues) {
      handleTempSpeedLimitTypeChange(changedValues.tempSpeedLimitType);
    }
  };
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (!vehicleName) {
        message.error('无法获取车辆信息');
        return;
      }

      setLoading(true);
      const requestBody: any = {
        vehicleName: vehicleName,
      };
      requestBody.isTempSpeedLimitEnabled =
        speedLimitInfo.isTempSpeedLimitEnabled;
      requestBody.tempSpeedLimitType = speedLimitInfo.tempSpeedLimitType;
      requestBody.tempSpeedLimit = speedLimitInfo.tempSpeedLimit;

      const res = await request({
        method: 'POST',
        path: '/monitor/web/command/setTempSpeedLimit',
        body: requestBody,
      });
      if (res.code === HttpStatusCode.Success) {
        message.success('车辆限速设置成功');
        handleCloseLimitSpeedModal();
      } else {
        message.error(res.message || '车辆限速设置失败');
      }
    } catch (error) {
      console.error('表单提交失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="车辆限速设置"
      open={visible}
      confirmLoading={loading}
      width={1000}
      className="vehicle-speed-limit-setting-modal"
      onCancel={handleCloseLimitSpeedModal}
      onOk={handleSubmit}
    >
      {speedLimitInfo && (
        <Form
          form={form}
          initialValues={{
            isTempSpeedLimitEnabled:
              speedLimitInfo.isTempSpeedLimitEnabled ?? false,
            tempSpeedLimitType: speedLimitInfo.tempSpeedLimitType,
            tempSpeedLimit: speedLimitInfo.tempSpeedLimit,
          }}
          labelCol={{
            span: 6,
          }}
          wrapperCol={{
            span: 17,
          }}
          onValuesChange={(changedValues, allValues) =>
            onValuesChange(changedValues)
          }
        >
          <Form.Item label="车端限速值(km/h)">
            {speedLimitInfo.xgflags == undefined ? '-' : speedLimitInfo.xgflags}
          </Form.Item>

          <Form.Item label="是否开启临时限速" name="isTempSpeedLimitEnabled">
            <Radio.Group>
              <Radio value={true}>是</Radio>
              <Radio value={false}>否</Radio>
            </Radio.Group>
          </Form.Item>

          {form.getFieldValue('isTempSpeedLimitEnabled') && (
            <Form.Item
              label="临时限速(km/h)"
              name="tempSpeedLimitType"
              rules={[{ required: true, message: '请选择临时限速' }]}
            >
              <Radio.Group options={tempSpeedLimitTypes} />
            </Form.Item>
          )}

          {speedLimitInfo?.tempSpeedLimitType === TempSpeedLimitType.OTHER && (
            <Form.Item
              label="自定义限速值(km/h)"
              name="tempSpeedLimit"
              rules={[
                { required: true, message: '请输入自定义限速值' },
                () => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (isNaN(value) || value <= 0) {
                      return Promise.reject('请输入有效的限速值');
                    }
                    if (
                      speedLimitInfo &&
                      value >= Number(speedLimitInfo.xgflags)
                    ) {
                      return Promise.reject('自定义限速值必须小于车端限速值');
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                placeholder="请输入自定义限速值"
                min={1}
                max={
                  speedLimitInfo
                    ? Number(speedLimitInfo.xgflags) - 1
                    : undefined
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default React.memo(SpeedLimitModal);
